import axios from "axios";
import queryString from "query-string";
import lodash from "lodash";
import * as appDataService from "@/AppDataService";
import * as utils from "@/Shared/utils";

import {
  API_RULE,
  API_RULE_SEARCH,
  API_RULE_SET,
  API_RULE_SET_SEARCH,
  API_XPATH_MAPPING,
  API_XPATH_ALL_MAPPING,
  API_RULE_COLLECTION,
  API_RULE_COLLECTION_SEARCH,
  API_TEST_VALIDATION_RULE,
  API_TEST_VALIDATION_RULE_SET,
  API_TEST_VALIDATION_RULE_COLLECTION,
  API_TEST_TRANSFORMATION_RULE,
  API_TEST_TRANSFORMATION_RULE_SET,
  API_TEST_TRANSFORMATION_RULE_COLLECTION,
  API_VALIDATE_XML,
  API_PROVIDER_CONFIGURATION
} from "./constants";

/***********************
 * Provider Data Service
 ***********************/

/**
 * Retrieve provider groups
 * @param {*} retrieveSecondaryOwners
 */
export function retrieveSecondaryOwners(queryObjectInput, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token;
      let queryObject = {};
      if (!lodash.isUndefined(queryObjectInput)) {
        queryObject = queryObjectInput;
      }
      queryObject["sort"] = "title";
      queryObject["token"] = token;
      queryObject["serviceURL"] = serviceURL;
      let providersURL = utils.getServerUrl() + API_PROVIDER_CONFIGURATION;
      providersURL += "?" + queryString.stringify(queryObject);
      let secondaryOwners = [];
      return axios
        .get(providersURL)
        .then(response => {
          let hasEntries = lodash.has(response, ["data", "entries"]);
          if (hasEntries) {
            secondaryOwners = response.data.entries;
          }
          // Special secondary owner 'Any' for rule management
          secondaryOwners.unshift({ title: "Any" });
          return secondaryOwners;
        })
        .catch(error => {
          secondaryOwners = [];
          return Promise.reject(error);
        });
    })
    .catch(error => {
      return Promise.reject(error);
    });
}

/*******************
 * Rule Data Service
 *******************/

/**
 * getRule - Retrieve rules
 * @param {string} ruleId
 * @returns {Promise}
 */
export function getRule(ruleId) {
  let rulesURL = utils.getServerUrl() + API_RULE;
  rulesURL += "/" + ruleId;
  let ruleResponse = {};
  return axios
    .get(rulesURL)
    .then(response => {
      ruleResponse = response;
      return ruleResponse;
    })
    .catch(error => {
      ruleResponse = {};
      return Promise.reject(error);
    });
}

/**
 * searchRule - Search for rules
 * @param {object} queryObject
 * @returns {Promise}
 */
export function searchRule(queryObject) {
  let rulesURL = utils.getServerUrl() + API_RULE_SEARCH;
  rulesURL += "?" + queryString.stringify(queryObject);
  let ruleResponse = {};
  return axios
    .get(rulesURL)
    .then(response => {
      ruleResponse = response;
      return ruleResponse;
    })
    .catch(error => {
      ruleResponse = {};
      return Promise.reject(error);
    });
}

/**
 * createRule - Create Rule
 * @param {object} data
 * @returns {Promise}
 */
export function createRule(data) {
  let ruleURL = utils.getServerUrl() + API_RULE;
  let ruleResponse = {};
  return axios
    .post(ruleURL, data)
    .then(response => {
      ruleResponse = response;
      return ruleResponse;
    })
    .catch(error => {
      ruleResponse = {};
      return Promise.reject(error);
    });
}

/**
 * updateRule - Update Rule
 * @param {string} ruleId
 * @param {object} data
 * @returns {Promise}
 */
export function updateRule(ruleId, data) {
  let ruleURL = utils.getServerUrl() + API_RULE;
  ruleURL += "/" + ruleId;
  let ruleResponse = {};
  return axios
    .put(ruleURL, data)
    .then(response => {
      ruleResponse = response;
      return ruleResponse;
    })
    .catch(error => {
      ruleResponse = {};
      return Promise.reject(error);
    });
}

/**
 * deleteRule - Delete Rule
 * @param {string} ruleId
 * @returns {Promise}
 */
export function deleteRule(ruleId) {
  let ruleURL = utils.getServerUrl() + API_RULE;
  ruleURL += "/" + ruleId;
  let ruleResponse = {};
  return axios
    .delete(ruleURL)
    .then(response => {
      ruleResponse = response;
      return ruleResponse;
    })
    .catch(error => {
      ruleResponse = {};
      return Promise.reject(error);
    });
}

/***********************
 * Rule Set Data Service
 ***********************/

/**
 * getRuleSet - Retrieve rule set
 * @param {string} ruleSetId
 * @returns {Promise}
 */
export function getRuleSet(ruleSetId) {
  let ruleSetURL = utils.getServerUrl() + API_RULE_SET;
  ruleSetURL += "/" + ruleSetId;
  let ruleSetResponse = {};
  return axios
    .get(ruleSetURL)
    .then(response => {
      ruleSetResponse = response;
      return ruleSetResponse;
    })
    .catch(error => {
      ruleSetResponse = {};
      return Promise.reject(error);
    });
}

/**
 * searchRuleSet - Search for rules sets
 * @param {object} queryObject
 * @returns {Promise}
 */
export function searchRuleSet(queryObject) {
  let ruleSetURL = utils.getServerUrl() + API_RULE_SET_SEARCH;
  ruleSetURL += "?" + queryString.stringify(queryObject);
  let ruleSetResponse = {};
  return axios
    .get(ruleSetURL)
    .then(response => {
      ruleSetResponse = response;
      return ruleSetResponse;
    })
    .catch(error => {
      ruleSetResponse = {};
      return Promise.reject(error);
    });
}

/**
 * createRuleSet - Create Rule set
 * @param {object} data
 * @returns {Promise}
 */
export function createRuleSet(data) {
  let ruleSetURL = utils.getServerUrl() + API_RULE_SET;
  let ruleSetResponse = {};
  return axios
    .post(ruleSetURL, data)
    .then(response => {
      ruleSetResponse = response;
      return ruleSetResponse;
    })
    .catch(error => {
      ruleSetResponse = {};
      return Promise.reject(error);
    });
}

/**
 * updateRuleSet - Update Rule set
 * @param {string} ruleSetId
 * @param {object} data
 * @returns {Promise}
 */
export function updateRuleSet(ruleSetId, data) {
  let ruleSetURL = utils.getServerUrl() + API_RULE_SET;
  ruleSetURL += "/" + ruleSetId;
  let ruleSetResponse = {};
  return axios
    .put(ruleSetURL, data)
    .then(response => {
      ruleSetResponse = response;
      return ruleSetResponse;
    })
    .catch(error => {
      ruleSetResponse = {};
      return Promise.reject(error);
    });
}

/**
 * deleteRuleSet - Delete Rule set
 * @param {string} ruleSetId
 * @returns {Promise}
 */
export function deleteRuleSet(ruleSetId) {
  let ruleSetURL = utils.getServerUrl() + API_RULE_SET;
  ruleSetURL += "/" + ruleSetId;
  let ruleSetResponse = {};
  return axios
    .delete(ruleSetURL)
    .then(response => {
      ruleSetResponse = response;
      return ruleSetResponse;
    })
    .catch(error => {
      ruleSetResponse = {};
      return Promise.reject(error);
    });
}

/******************************
 * Rule Collection Data Service
 ******************************/
/**
 * getRuleCollection - Retrieve rule sets
 * @param {string} ruleCollectionId
 * @returns {Promise}
 */
export function getRuleCollection(ruleCollectionId) {
  let ruleCollectionURL = utils.getServerUrl() + API_RULE_COLLECTION;
  ruleCollectionURL += "/" + ruleCollectionId;
  let ruleCollectionResponse = {};
  return axios
    .get(ruleCollectionURL)
    .then(response => {
      ruleCollectionResponse = response;
      return ruleCollectionResponse;
    })
    .catch(error => {
      ruleCollectionResponse = {};
      return Promise.reject(error);
    });
}

/**
 * searchRuleCollection - Search for rules collections
 * @param {object} queryObject
 * @returns {Promise}
 */
export function searchRuleCollection(queryObject) {
  let ruleCollectionURL = utils.getServerUrl() + API_RULE_COLLECTION_SEARCH;
  ruleCollectionURL += "?" + queryString.stringify(queryObject);
  let ruleCollectionResponse = {};
  return axios
    .get(ruleCollectionURL)
    .then(response => {
      ruleCollectionResponse = response;
      return ruleCollectionResponse;
    })
    .catch(error => {
      ruleCollectionResponse = {};
      return Promise.reject(error);
    });
}

/**
 * createRuleCollection - Create RuleCollection
 * @param {object} data
 * @returns {Promise}
 */
export function createRuleCollection(data) {
  let ruleCollectionURL = utils.getServerUrl() + API_RULE_COLLECTION;
  let ruleCollectionResponse = {};
  return axios
    .post(ruleCollectionURL, data)
    .then(response => {
      ruleCollectionResponse = response;
      return ruleCollectionResponse;
    })
    .catch(error => {
      ruleCollectionResponse = {};
      return Promise.reject(error);
    });
}

/**
 * updateRuleCollection - Update Rule Collection
 * @param {string} ruleCollectionId
 * @param {object} data
 * @returns {Promise}
 */
export function updateRuleCollection(ruleCollectionId, data) {
  let ruleCollectionURL = utils.getServerUrl() + API_RULE_COLLECTION;
  ruleCollectionURL += "/" + ruleCollectionId;
  let ruleCollectionResponse = {};
  return axios
    .put(ruleCollectionURL, data)
    .then(response => {
      ruleCollectionResponse = response;
      return ruleCollectionResponse;
    })
    .catch(error => {
      ruleCollectionResponse = {};
      return Promise.reject(error);
    });
}

/**
 * deleteRuleCollection - Delete RuleSet
 * @param {*} ruleCollectionId
 * @param {string} accountId
 * @returns {Promise}
 */
export function deleteRuleCollection(ruleCollectionId) {
  let ruleCollectionURL = utils.getServerUrl() + API_RULE_COLLECTION;
  ruleCollectionURL += "/" + ruleCollectionId;
  let ruleCollectionResponse = {};
  return axios
    .delete(ruleCollectionURL)
    .then(response => {
      ruleCollectionResponse = response;
      return ruleCollectionResponse;
    })
    .catch(error => {
      ruleCollectionResponse = {};
      return Promise.reject(error);
    });
}

/****************************
 * Xpath Mapping Data Service
 ****************************/
/**
 * Retrieve XPath Mappings
 * @param {string} key
 * @returns {Promise}
 */
export function getXPathMapping(key) {
  let xPathUrl = utils.getServerUrl() + API_XPATH_MAPPING;
  if (!lodash.isUndefined(key)) {
    xPathUrl += "/" + key;
  }
  let keyValuePairsResponse = {};
  return axios
    .get(xPathUrl)
    .then(response => {
      keyValuePairsResponse = response;
      return keyValuePairsResponse;
    })
    .catch(error => {
      keyValuePairsResponse = {};
      return Promise.reject(error);
    });
}

export function getAllXPathMappings() {
  let xPathUrl = utils.getServerUrl() + API_XPATH_ALL_MAPPING;
  let keyValuePairsResponse = {};
  return axios
    .get(xPathUrl)
    .then(response => {
      keyValuePairsResponse = response;
      return keyValuePairsResponse;
    })
    .catch(error => {
      keyValuePairsResponse = {};
      return Promise.reject(error);
    });
}

/**
 * Add XPath Mapping
 * @param {string} key
 * @param {string} configValue
 * @returns {Promise}
 */
export function createXPathMapping(key, configValue) {
  let xPathUrl = utils.getServerUrl() + API_XPATH_MAPPING;
  xPathUrl += "/" + key;
  let keyValuePairResponse = {};

  return axios
    .post(xPathUrl, configValue)
    .then(response => {
      keyValuePairResponse = response;
      return keyValuePairResponse;
    })
    .catch(error => {
      keyValuePairResponse = {};
      return Promise.reject(error);
    });
}

/**
 * Update Mapping
 * @param {string} key
 * @param {string} configValue
 * @returns {Promise}
 */
export function updateXPathMapping(key, configValue) {
  let xPathUrl = utils.getServerUrl() + API_XPATH_MAPPING;
  xPathUrl += "/" + key;
  let keyValuePairResponse = {};

  return axios
    .put(xPathUrl, configValue)
    .then(response => {
      keyValuePairResponse = response;
      return keyValuePairResponse;
    })
    .catch(error => {
      keyValuePairResponse = {};
      return Promise.reject(error);
    });
}

/**
 * Add XPath Mapping
 * @param {string} key
 * @returns {Promise}
 */
export function deleteXPathMapping(key) {
  let xPathUrl = utils.getServerUrl() + API_XPATH_MAPPING;
  xPathUrl += "/" + key;
  let keyValuePairResponse = {};

  return axios
    .delete(xPathUrl)
    .then(response => {
      keyValuePairResponse = response;
      return keyValuePairResponse;
    })
    .catch(error => {
      keyValuePairResponse = {};
      return Promise.reject(error);
    });
}

/*************
 * Web Service
 *************/

/**
 * testValidationRule - test validation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testValidationRule(data) {
  let testRuleURL = utils.getServerUrl() + API_TEST_VALIDATION_RULE;
  let testRuleResponse = {};
  return axios
    .post(testRuleURL, data)
    .then(response => {
      testRuleResponse = response;
      return testRuleResponse;
    })
    .catch(error => {
      testRuleResponse = {};
      return Promise.reject(error);
    });
}

/**
 * testValidationRuleSet - test validation rule set
 * @param {object} data
 * @returns {Promise}
 */
export function testValidationRuleSet(data) {
  let testRuleURL = utils.getServerUrl() + API_TEST_VALIDATION_RULE_SET;
  let testRuleResponse = {};
  return axios
    .post(testRuleURL, data)
    .then(response => {
      testRuleResponse = response;
      return testRuleResponse;
    })
    .catch(error => {
      testRuleResponse = {};
      return Promise.reject(error);
    });
}

/**
 * testValidationRuleCollection - test validation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testValidationRuleCollection(data) {
  let testRuleURL = utils.getServerUrl() + API_TEST_VALIDATION_RULE_COLLECTION;
  let testRuleResponse = {};
  return axios
    .post(testRuleURL, data)
    .then(response => {
      testRuleResponse = response;
      return testRuleResponse;
    })
    .catch(error => {
      testRuleResponse = {};
      return Promise.reject(error);
    });
}

/**
 * testTransformationRule - test transformation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testTransformationRule(data) {
  let testRuleURL = utils.getServerUrl() + API_TEST_TRANSFORMATION_RULE;
  let testRuleResponse = {};
  return axios
    .post(testRuleURL, data)
    .then(response => {
      testRuleResponse = response;
      return testRuleResponse;
    })
    .catch(error => {
      testRuleResponse = {};
      return Promise.reject(error);
    });
}

/**
 * testTransformationRuleSet - test validation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testTransformationRuleSet(data) {
  let testRuleURL = utils.getServerUrl() + API_TEST_TRANSFORMATION_RULE_SET;
  let testRuleResponse = {};
  return axios
    .post(testRuleURL, data)
    .then(response => {
      testRuleResponse = response;
      return testRuleResponse;
    })
    .catch(error => {
      testRuleResponse = {};
      return Promise.reject(error);
    });
}

/**
 * testTransformationRuleCollection - test validation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testTransformationRuleCollection(data) {
  let testRuleURL =
    utils.getServerUrl() + API_TEST_TRANSFORMATION_RULE_COLLECTION;
  let testRuleResponse = {};
  return axios
    .post(testRuleURL, data)
    .then(response => {
      testRuleResponse = response;
      return testRuleResponse;
    })
    .catch(error => {
      testRuleResponse = {};
      return Promise.reject(error);
    });
}

/**
 * validateXML - validate XML
 * @param {object} data
 * @returns {Promise}
 */
export function validateXML(xmlString) {
  let validateXMLURL = utils.getServerUrl() + API_VALIDATE_XML;
  let validationResponse = {};
  return axios
    .post(validateXMLURL, {
      xml: xmlString
    })
    .then(response => {
      validationResponse = response;
      return validationResponse;
    })
    .catch(error => {
      validationResponse = {};
      return Promise.reject(error);
    });
}



// WEBPACK FOOTER //
// ./src/XVAT/XVATDataService.js